import { useEffect, useMemo, useState } from "react";
import { Loader2, Download, Package } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DrawerSection } from "@/components/forms/fields";
import { inboundService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";

export function ReceiveDrawer({ open, onOpenChange, record, storeName, onSaved }) {
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && record) {
      setLines((record.products || []).map((p) => ({
        sku: p.sku,
        product_name: p.product_name || "",
        expected: p.expected_qty || 0,
        prevReceived: p.received_qty || 0,
        confirmed: !!p.confirmed,
        received: p.confirmed || (p.received_qty || 0) > 0 ? String(p.received_qty || 0) : String(p.expected_qty || 0),
        condition: p.condition || "good",
        note: p.note || "",
      })));
    }
  }, [open, record]);

  const setLine = (i, key, value) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)));

  const hasChange = useMemo(
    () => lines.some((l) => (Number(l.received) || 0) > (l.prevReceived || 0)),
    [lines]
  );
  const canSave = hasChange && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const products = lines
        .filter((l) => (Number(l.received) || 0) > (l.prevReceived || 0))
        .map((l) => ({
          sku: l.sku,
          received_qty: Number(l.received) || 0,
          condition: l.condition,
          note: (l.note || "").trim(),
        }));
      const res = await inboundService.confirm(record.id, products);
      const status = res?.inbound?.status;
      toast.success(status === "received" ? "Shipment fully received — inventory updated" : "Receipt recorded — inventory updated");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const trackingNumbers = record?.tracking_numbers || [];

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={Download}
      title="Receive Shipment"
      description={record ? `${record.inbound_id} · ${storeName || "—"}` : ""}
      widthClass="sm:max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="receive-drawer-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="receive-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm receipt"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {(record?.supplier || trackingNumbers.length > 0) && (
          <DrawerSection title="Shipment">
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
              {record?.supplier && <p className="text-foreground"><span className="text-muted-foreground">Supplier:</span> {record.supplier}</p>}
              {trackingNumbers.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="receive-tracking">
                  {trackingNumbers.map((t, i) => (
                    <span key={i} className="rounded-md bg-background px-2 py-0.5 text-xs font-medium text-foreground ring-1 ring-border">{t}</span>
                  ))}
                </div>
              )}
            </div>
          </DrawerSection>
        )}

        <DrawerSection title="Items">
          <p className="text-xs text-muted-foreground">
            Enter the quantity physically received. Partial receiving is supported — confirmed quantities are locked and remaining units can be received later.
          </p>
          <div className="space-y-3">
            {lines.map((l, i) => {
              const received = Number(l.received) || 0;
              const remaining = Math.max(0, l.expected - received);
              const over = received > l.expected;
              return (
                <div key={i} className="rounded-lg border border-border bg-card p-3 shadow-soft" data-testid={`receive-line-${i}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-semibold text-foreground">{l.sku}</span>
                      {l.product_name && <span className="text-sm text-muted-foreground">· {l.product_name}</span>}
                    </div>
                    {l.confirmed && <span className="text-xs font-medium text-success-dark">Locked at {l.prevReceived}</span>}
                  </div>
                  <div className="grid grid-cols-12 items-end gap-2">
                    <div className="col-span-3">
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">Expected</label>
                      <div className="flex h-10 items-center rounded-lg border border-input bg-muted/40 px-3 text-sm tabular-nums text-foreground" data-testid={`receive-expected-${i}`}>{l.expected}</div>
                    </div>
                    <div className="col-span-3">
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">Received</label>
                      <Input type="number" min={l.confirmed ? l.prevReceived : 0} value={l.received}
                        onChange={(e) => setLine(i, "received", e.target.value)} data-testid={`receive-qty-${i}`} />
                    </div>
                    <div className="col-span-2">
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">Diff</label>
                      <div className={`flex h-10 items-center rounded-lg px-3 text-sm font-medium tabular-nums ${over ? "text-primary" : remaining > 0 ? "text-warning-dark" : "text-success-dark"}`} data-testid={`receive-diff-${i}`}>
                        {over ? `+${received - l.expected}` : remaining > 0 ? `-${remaining}` : "0"}
                      </div>
                    </div>
                    <div className="col-span-4">
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">Condition</label>
                      <Select value={l.condition} onValueChange={(v) => setLine(i, "condition", v)}>
                        <SelectTrigger data-testid={`receive-condition-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="good">Good</SelectItem>
                          <SelectItem value="damaged">Damaged</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <Input value={l.note} onChange={(e) => setLine(i, "note", e.target.value)} placeholder="Optional note (e.g. box crushed)" data-testid={`receive-note-${i}`} />
                  </div>
                  {l.condition === "damaged" && received > l.prevReceived && (
                    <p className="mt-1.5 text-xs font-medium text-warning-dark">Damaged units are recorded but not added to available stock.</p>
                  )}
                </div>
              );
            })}
          </div>
        </DrawerSection>
      </div>
    </Drawer>
  );
}
